import * as THREE from "three";
import { anyCheat, gameSpeed } from "./cheats";
import { GoalsDone, type ChallengeHud, type ChallengePrompt, type ChallengeSpot } from "./challenge";
import { ChallengeBoard } from "./challenge-board";
import type { LandedCombo } from "./combos";
import { DEMO_LINES, DEMO_STEP, demoSkater } from "./demo";
import { placeCollectables } from "./collectables";
import { BUTTONS, InputManager, touchOnly, type InputFrame } from "./input";
import { phoneScreen, type HillHud } from "./hill";
import { HillSession } from "./hill-session";
import { hillDrop, surfaceAt, type Level } from "./level";
import type { MapFile } from "./mapfile";
import { DEFAULT_MAP_ID, HILL, loadMapDef, mapFromFile, mapInfo, type MapDef } from "./maps";
import { HILL_SITE } from "./site";
import { HubGuide, type HubGuideHud, type HubListHud, type HubSpot } from "./hubs";
import type { Tier } from "./ladder";
import { MenuController, type MenuContext, type MenuIntent, type MenuStep, type SpecialEdit } from "./menu";
import type { ControlRows, PauseAction, PauseMode, PauseScreen } from "./pause";
import type { Goal, RunPhase } from "./run";
import { ChallengeSession, DemoSession, FreeSkateSession, RunSession, type GoalSession, type Session, type SessionHost } from "./session";
import { ContactBlob } from "./render/atmosphere";
import { BailFx } from "./render/bail-fx";
import { PaintFx } from "./render/paint";
import { WashFx } from "./render/wash";
import { Crosshair } from "./render/crosshair";
import { GraffitiMeshes } from "./render/graffiti";
import { TargetMeshes } from "./render/targets";
import { PropMeshes } from "./render/props";
import { placeProps } from "./props";
import { heldItem, type ItemId } from "./sim/backpack";
import { dueHint, HINT_TIME, hintText, packKeysText, type HintId } from "./ride-hints";
import type { RideId } from "./sim/rides";
import { TrickFx } from "./render/trick-fx";
import { FinisherCam } from "./render/finisher-cam";
import { chaseFov, ChaseCamera, FOV_EASE, hillView } from "./render/camera";
import { MarkerMeshes } from "./render/markers";
import { PickupMeshes } from "./render/pickups";
import { VehicleMeshes } from "./render/vehicles";
import { HillRoadMeshes } from "./render/hill-course";
import { randomSeed } from "./hill-seed";
import { peopleRider } from "./render/pedestrians";
import { SkaterModel } from "./render/skater-model";
import { Stage } from "./render/stage";
import { disposeRider } from "./render/skater-parts";
import { activeStyle, setActiveStyle, styleFromUrl } from "./render/styles";
import { Skater, stanceReadout, type SkaterEvent } from "./skater";
import { findSkater, loadSkaterPick, saveSkaterPick } from "./skaters";
import { CAMERA_RIGS, CosmeticPicker, effectiveQuality, keyLabel, loadSettings, padLabel, pickQuick, saveSettings, stepArtStyle, VOLUME_MAX, type Action, type QuickSetting, type Settings } from "./settings";
import { StatSheet, type StatsHud } from "./stats";
import { CueTracker, loopLevels, soundSnap, type Cue } from "./sound/cues";
import { runOutLeft } from "./sim/foot";
import { hillDropIn, hillStepRide, startDropsIn } from "./sim/hill";
import { Sfx } from "./sound/sfx";
import { HAPTICS, type TouchPad } from "./touch";
import { touchHints, type TouchHints } from "./touch-hints";
import { storage } from "./storage";

export interface HudMessage {
  id: number;
  text: string;
  kind: "landed" | "bail" | "gap" | "goal";
}

/** A 2-minute run in progress (or just over), for the HUD's timer and the results screen. */
export type { ChallengeHud, ChallengePrompt } from "./challenge";

export interface RunHud {
  /** Whole seconds left, rounded up (0 after the buzzer). */
  seconds: number;
  phase: RunPhase;
  score: number;
  goalsDone: number;
}

/** A gap on the current map, and whether it's been landed this session. */
export interface GapStatus {
  name: string;
  points: number;
  found: boolean;
}

export type { StatsHud } from "./stats";

/** The Settings screen's state (#36). */
export interface SettingsHud {
  settings: Readonly<Settings>;
  /** The action waiting for its new button or key, or null. */
  rebinding: Action | null;
  /** A special's taps being set on Settings > Controls > Specials, or why they were refused (#135). */
  special?: SpecialEdit | null;
}

/** Everything React draws. `TouchHints` light up the touch buttons (#67). */
export interface HudState extends TouchHints {
  score: number;
  bestCombo: number;
  /** The best combo's full trick string ("" until one lands). */
  bestComboText: string;
  /** This session's landed combos, newest first. */
  recentCombos: readonly LandedCombo[];
  /** Special meter, 0..1 in steps of 0.01. */
  special: number;
  /** The meter is full: special tricks are unlocked and it glows. */
  specialFull: boolean;
  comboText: string;
  comboBase: number;
  comboMult: number;
  balance: number | null;
  /** Manual balance needle, -1 (tail) .. 1 (nose), while manualing. */
  manualBalance: number | null;
  /** A run out's time left (#19), 1 .. 0 in steps of 0.02, while a combo waits on foot. */
  runOut: number | null;
  /** The backpack's item in your hands on foot (#136), or null on the board... */
  packItem: ItemId | null;
  /** ...and the buttons that switch items and use the one that's out, for the HUD strip. */
  packKeys: string | null;
  /** What you're riding, or last rode if you're on foot (#141): the controls help shows the bike's tricks on the BMX. */
  ride: RideId;
  /** A one-time hint for the ride you just got on (#150), in the input you're using, or null. */
  rideHint: string | null;
  message: HudMessage | null;
  padName: string | null;
  /** A pad has connected on this device, now or before (#161). */
  padSeen: boolean;
  padBlocked: boolean;
  /** "Switch", "Nollie", both, or null when riding regular. */
  stance: string | null;
  paused: boolean;
  /** Highlighted pause menu item. */
  pauseIndex: number;
  /** Which screen of the pause menu is showing. */
  pauseScreen: PauseScreen;
  /** The map being skated. */
  mapId: string;
  /** The name of the map being loaded, while one is. */
  loading: string | null;
  /** The current map's gaps, for the pause menu's Gap list. */
  gaps: readonly GapStatus[];
  /** The demo line playing, or null when someone's skating. */
  demo: string | null;
  /** The 2-minute run, or null in free skate. */
  run: RunHud | null;
  /** The run's goals, or the map's in free skate (done if done this session, in a run or a challenge), for the Goals / Challenges screen and results. */
  goals: readonly Goal[];
  /** The free-skate challenge on, or null. */
  challenge: ChallengeHud | null;
  /** The marker or hub the skater's standing in, or null. */
  prompt: ChallengePrompt | null;
  /** A hub's list of challenges (#86), while it's open. */
  hub: HubListHud | null;
  /** The map has challenge hubs: a challenge's pass / fail card offers "Back to hub". */
  hasHubs: boolean;
  /** The arrow to a newly unlocked hub (#87), in free skate, or null. */
  guide: HubGuideHud | null;
  /** The open hubs' tiers on Pause > Challenges, one "Go to hub" row each (#87). */
  pauseHubs: readonly Tier[];
  /** Which control rows Settings > Controls shows on this device (#126). */
  pauseControls?: ControlRows;
  source: InputFrame["source"];
  speed: number;
  settings: SettingsHud;
  stats: StatsHud;
  /** Who's being ridden (#64). */
  skater: string;
  /** Bomb the Hill (#193): the run down the hill, on a hill map; null anywhere else. */
  hill: HillHud | null;
  /** A new art style or quality is being built (a frame or two, plus the rebuild itself): the menu says "Applying…". */
  applying: boolean;
}

const STEP = DEMO_STEP;
/** How far the speed wobbles (#193) rock the rider (radians) and shake the camera (m) at their worst. */
const WOBBLE_TILT = 0.14;
const WOBBLE_CAM = 0.12;
/** The rider leans into a carve (#193) this much further than into an ordinary turn. */
const CARVE_LEAN = 1.6;
/**
 * Frames a new art style or quality waits before it's built: React draws
 * "Applying…" after the frame that asked, and the browser paints it after the
 * next, before the rebuild holds the main thread.
 */
const LOOK_DELAY = 2;
const MAX_STEPS = 12;

export class Game {
  /** The renderer, lights, environment, park meshes and post chain (#75). */
  private stage: Stage;
  private scene: THREE.Scene;
  private chase = new ChaseCamera();
  private input = new InputManager();
  /** Unset until the first map has loaded. */
  private map!: MapDef;
  private level!: Level;
  private skater!: Skater;
  /** Who's being ridden (#64): the last pick, remembered where storage allows. */
  private skaterId = loadSkaterPick(storage());
  private model = new SkaterModel(findSkater(this.skaterId).look);
  /** The model's turn on the turntable behind the start screen and Pause > Skater, radians. */
  private turntable = 0;
  private blob = new ContactBlob();
  private raf = 0;
  private last = 0;
  private acc = 0;
  private pending: InputFrame | null = null;
  /** The pause menu, rebinding, results screen and challenge cards. */
  private menu = new MenuController();
  /** Nothing moves until the player clicks in (or a pad shows up). */
  private running = false;
  /** Ignore the ollie button until it's let go, so picking "Resume" with X doesn't pop an ollie. */
  private swallowOllie = false;
  private message: HudMessage | null = null;
  private messageId = 0;
  private hud: HudState | null = null;
  /** The map being fetched, if any; only the latest request gets applied. */
  private loading: { id: string; name: string } | null = null;
  private loadSeq = 0;
  /** Gap names landed this session, per map id (not by the demo). */
  private gapsFound = new Map<string, Set<string>>();
  /** Bomb the Hill (#193, #218): the bests down each hill, the HUD for the run on one and the tutorial. */
  private hill = new HillSession();
  /** The park last loaded, back behind the lobby when you leave the hill for it (#224). */
  private parkId = DEFAULT_MAP_ID;
  private shakeAxis = new THREE.Vector3();
  private gapList: readonly GapStatus[] = [];
  /** Free skate, kept for the whole session: it holds the map's markers. */
  private free = new FreeSkateSession();
  /** What's on: free skate, the demo, a 2-minute run or a challenge. */
  private session: Session = this.free;
  private goalList: readonly Goal[] = [];
  /** The session's letters and tape in the park; null in free skate. */
  private pickups: PickupMeshes | null = null;
  /** The map's vehicles (#37), driven by the skater's clock. */
  private vehicles: VehicleMeshes | null = null;
  /** Bomb the Hill's road (#194, #195): what's in it and the traffic, on a hill with a course. */
  private road: HillRoadMeshes | null = null;
  /** Goals done this session, in runs and challenges, per map. */
  private goalsDone = new GoalsDone();
  /** The map on is flipped left to right (Mirror mode, #119). */
  private mapMirrored = false;
  /** ...and the map loading is, while one is (`loading`). */
  private loadingMirrored = false;
  /** A cheat was on at some point during the run or challenge on (#119): its goals don't count. */
  private cheated = false;
  /** The map's challenges (#88): start spots, hubs, the ladder (#66) and its locks. */
  private board = ChallengeBoard.empty(this.goalsDone);
  /** The guide arrow to the hub a goal just unlocked (#87), until it's reached or times out. */
  private guide: HubGuide | null = null;
  /** The ride under you last step (null on foot), to see you get on one; and a hint that's up (#150). */
  private rideWas: RideId | null = null;
  private hint: { id: HintId; left: number } | null = null;
  /** The camera's heading on the ground, for the guide arrow (kept to skip an allocation a frame). */
  private camDir = new THREE.Vector3();
  /** The map's challenge markers' (or hubs') meshes (shown in free skate only). */
  private markers: MarkerMeshes | null = null;
  private resizeObserver: ResizeObserver;
  /** Volume, camera and button bindings (#36), saved when storage allows. */
  private settings: Settings = loadSettings(storage());
  /** Picks the cosmetics set to Random (#123), fresh each time one plays (#128). Render-only, so the sim and demo lines never see it. */
  private readonly picker = new CosmeticPicker(Math.random);
  private settingsHud: SettingsHud = { settings: this.settings, rebinding: null, special: null };
  /**
   * Frames until a new art style or quality gets built, 0 when none is
   * waiting. The rebuild holds the main thread, so it waits for the menu to
   * paint "Applying…" first; `applySettings` calls in the meantime just
   * change what gets built (so the dev hotkey's presses fold into one).
   */
  private lookDue = 0;
  private sfx = new Sfx(this.settings.volume / VOLUME_MAX);
  /** The chosen bail style's effect (#83), played over the model's tumble. */
  private bailFx = new BailFx(this.picker);
  /** Grind sparks and landing bursts (#118), fed the sim steps' cues each frame. */
  private trickFx = new TrickFx(this.picker);
  /** Paintballs in flight and their splats on the park (#136). */
  private paintFx = new PaintFx(STEP);
  /** The power washer's water (#151). */
  private washFx = new WashFx();
  /** The paintball gun's crosshair (#143). */
  private crosshair = new Crosshair(STEP);
  /** The map's paintball targets (#137), built with the park. */
  private targets: TargetMeshes | null = null;
  /** The map's graffiti (#151), built with the park. */
  private graffiti: GraffitiMeshes | null = null;
  /** The map's cones and cans (#151), built with the park. */
  private props: PropMeshes | null = null;
  private fxCues: Cue[] = [];
  /** The slow-mo zoom finisher (#118): narrows the camera and slows the effects' clock, never the sim. */
  private finish = new FinisherCam();
  /** The chase view's own field of view (#235): wider upright, eased after a rotate; the hill's widening and the finisher go on top. */
  private viewFov = chaseFov(1);
  private floorAt = (x: number, z: number): number => surfaceAt(this.level, x, z).h;
  private cues = new CueTracker();
  /** The skater the cue tracker last saw: a new one (respawn, demo line, map) starts it over. */
  private cueSkater: Skater | null = null;
  /** The session's stats and stat points (#35): one pool of points, a sheet per skater (#64). */
  private statSheet = new StatSheet(findSkater(this.skaterId).stats, this.skaterId);

  constructor(
    private canvas: HTMLCanvasElement,
    private callbacks: {
      onHud: (hud: HudState) => void;
      onToggleHelp: () => void;
      /** Back to the lobby (#224): the page puts it up; `begin` drops in again. */
      onLobby?: () => void;
      /** The on-screen controls' fingers (#67), fed by `ui/TouchControls.tsx`. */
      touch?: TouchPad;
    },
  ) {
    if (callbacks.touch) this.input.touch = callbacks.touch;
    // `?style=realistic` picks the art style (#75), to share or bookmark a look.
    const urlStyle = styleFromUrl(window.location.search);
    if (urlStyle) this.settings = { ...this.settings, artStyle: urlStyle };
    this.stage = new Stage(canvas);
    this.scene = this.stage.scene;
    this.applySettings(this.settings, true);
    this.scene.add(this.model.root, this.blob.mesh, this.bailFx.root, this.trickFx.root, this.paintFx.root, this.washFx.root, this.crosshair.root);
    // The hill site (#219) has no parks: it opens on the hill.
    if (HILL_SITE) this.loadMapFile(HILL);
    else void this.loadMap(DEFAULT_MAP_ID);

    window.addEventListener("blur", this.onBlur);
    document.addEventListener("visibilitychange", this.onHidden);
    // Browsers only allow sound after a click or key press.
    window.addEventListener("pointerdown", this.unlockAudio, true);
    window.addEventListener("keydown", this.unlockAudio, true);
    if (import.meta.env.DEV) window.addEventListener("keydown", this.onDevKey);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
  }

  start(): void {
    this.last = performance.now();
    const tick = (now: number) => {
      this.raf = requestAnimationFrame(tick);
      this.frame(Math.min(0.1, (now - this.last) / 1000));
      this.last = now;
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** Starts the sim; until then the park renders but the skater waits. */
  begin(): void {
    this.running = true;
  }

  /**
   * Back to the lobby (#224), from the pause menu or the hill's end card:
   * a run, challenge or demo ends, the menus close, a fresh skater waits at
   * the spawn and the page puts the lobby up. The skater, ride and map picks
   * stay. From the hill, the park skated last comes back behind the lobby
   * (the hill site has no parks, so it stays on the hill, a run fresh from
   * the top). `begin` drops in again.
   */
  toLobby(): void {
    this.endRun();
    this.endChallenge();
    this.leaveDemo();
    this.menu.pause.hide();
    if (this.menu.hub.open) this.menu.hub.hide();
    this.running = false;
    this.pending = null;
    this.message = null;
    this.skater = this.newSkater();
    this.snapCamera();
    this.exposeDev();
    if (this.level.hill && !HILL_SITE) void this.loadMap(this.parkId);
    else if (this.level.hill) this.loadMapFile(HILL);
    this.callbacks.onLobby?.();
  }

  /**
   * Rides skater `id` from now on (#64): their model, their stat sheet, and
   * the pick saved for next time. The skater stays where they are.
   */
  selectSkater(id: string): void {
    const profile = findSkater(id);
    if (profile.id === this.skaterId) return;
    this.skaterId = profile.id;
    saveSkaterPick(storage(), profile.id);
    this.statSheet.select(profile.id, profile.stats);
    // Demo skaters ride the default stats; everyone else rides the sheet.
    if (this.session.kind !== "demo" && this.skater) this.skater.stats = this.statSheet.stats;
    this.rebuildRider();
  }

  /** Builds the rider again (another skater, or another art style), facing the same way. */
  private rebuildRider(): void {
    const old = this.model;
    this.bailFx.reset();
    this.scene.remove(old.root);
    disposeRider(old.root);
    this.model = new SkaterModel(findSkater(this.skaterId).look);
    this.model.root.rotation.y = old.root.rotation.y;
    this.scene.add(this.model.root);
    if (this.skater) this.model.update(this.skater, 1, 0);
  }

  /** Switches the art style live (#75): renderer, lights, park and rider, without reloading the map. */
  private useStyle(id: Settings["artStyle"]): void {
    if (activeStyle().id === id) return;
    setActiveStyle(id);
    this.stage.applyStyle();
    if (this.map) this.buildWorld();
    this.rebuildRider();
  }

  /**
   * Fetches a map's file and switches to it (see `useMap`), flipped if
   * `mirrored` (the Mirror mode cheat, #119; demo lines never are). The sim
   * holds still meanwhile and the HUD says what's loading. Resolves true once
   * the map is in, false if a later load replaced this one or the file failed.
   */
  async loadMap(id: string, mirrored = this.settings.cheats.mirror): Promise<boolean> {
    const info = mapInfo(id);
    const seq = ++this.loadSeq;
    this.loading = { id: info.id, name: info.name };
    this.loadingMirrored = mirrored;
    try {
      const map = await loadMapDef(info.id, mirrored);
      if (seq !== this.loadSeq) return false;
      this.mapMirrored = mirrored;
      this.useMap(map);
      this.parkId = info.id;
      return true;
    } catch (e) {
      if (seq !== this.loadSeq) return false;
      console.error(e);
      if (this.map) this.message = { id: ++this.messageId, kind: "bail", text: `Couldn't load ${info.name}` };
      return false;
    } finally {
      if (seq === this.loadSeq) this.loading = null;
    }
  }

  /**
   * Dev only: skates a checked map file dropped or pasted in (see
   * DevMapLoader, which reads it and reports on it).
   */
  loadMapFile(file: MapFile): void {
    ++this.loadSeq;
    this.loading = null;
    this.leaveDemo();
    this.mapMirrored = false;
    this.useMap(mapFromFile(file));
  }

  /**
   * Switches to a loaded map without reloading the page: rebuilds the park
   * meshes, sky and lighting, and starts a fresh skater at the new spawn
   * (score, special and combo history start over, like a new THPS level).
   */
  private useMap(map: MapDef): void {
    this.map = map;
    this.level = map.level();
    // Bomb the Hill (#195): a new road every run, the first one too, each on a random seed.
    const road = this.level.hill?.road;
    if (road) {
      road.pickSeed = randomSeed;
      road.reset(randomSeed());
    }
    this.refreshGaps();
    // The backpack remembers its item across maps (#136); the paint stays behind.
    const item = (this.skater as Skater | undefined)?.pack.item;
    this.skater = this.newSkater();
    if (item) this.skater.pack.item = item;
    this.paintFx.clear();
    this.message = null;
    this.endRun();
    this.endChallenge();
    this.menu.hub.hide();
    this.board = ChallengeBoard.forMap(map, this.level, this.goalsDone);
    this.guide = null;
    this.hill.mapLoaded(this.level);
    // A hill (#193) is ridden for distance: no challenge markers or hubs on it.
    this.free.spots = this.level.hill ? [] : this.board.freeSpots;
    this.free.hubs = this.level.hill ? [] : this.board.hubs;
    this.free.needs = this.board.needs;
    if (this.markers) {
      this.scene.remove(this.markers.root);
      this.markers.dispose();
    }
    this.markers = new MarkerMeshes(this.board.markerSpots);
    this.scene.add(this.markers.root);
    this.refreshGoals();

    this.buildWorld();

    // Pose the rider at the spawn now (a long dt settles the eases): nothing
    // updates the model behind the start screen or the pause menu.
    this.model.update(this.skater, 1, 0);
    this.blob.update(this.level, this.skater);
    this.vehicles?.update(this.level, this.skater.clock);
    this.road?.update(this.skater.clock, peopleRider(this.skater));
    const f = this.skater.forward;
    this.chase.face(f.x, f.z);
    this.snapCamera();
    this.pending = null;

    this.exposeDev();
  }

  /**
   * The map's park, walls, sky, fog and lighting in the active art style,
   * replacing whatever was there.
   */
  private buildWorld(): void {
    this.stage.build(this.level, this.map.look, this.map.hubs);
    if (this.vehicles) {
      this.scene.remove(this.vehicles.root);
      this.vehicles.dispose();
      this.vehicles = null;
    }
    if (this.level.vehicles) {
      this.vehicles = new VehicleMeshes(this.level.vehicles);
      this.scene.add(this.vehicles.root);
      this.vehicles.update(this.level, this.skater.clock);
    }
    if (this.road) {
      this.scene.remove(this.road.root);
      this.road.dispose();
      this.road = null;
    }
    const road = this.level.hill?.road;
    if (road) {
      this.road = new HillRoadMeshes(road, this.level.hill!.grade, this.map.look);
      this.scene.add(this.road.root);
      this.road.update(this.skater.clock, peopleRider(this.skater));
    }
    if (this.targets) {
      this.scene.remove(this.targets.root);
      this.targets.dispose();
      this.targets = null;
    }
    if (this.level.targets) {
      this.targets = new TargetMeshes(this.level.targets);
      this.scene.add(this.targets.root);
      this.targets.update(this.skater.pack.burst, 0);
    }
    if (this.graffiti) {
      this.scene.remove(this.graffiti.root);
      this.graffiti.dispose();
      this.graffiti = null;
    }
    if (this.level.graffiti) {
      this.graffiti = new GraffitiMeshes(this.level.graffiti);
      this.scene.add(this.graffiti.root);
      this.graffiti.update(this.skater.pack, [], 0);
    }
    if (this.props) {
      this.scene.remove(this.props.root);
      this.props.dispose();
      this.props = null;
    }
    if (this.level.props) {
      const props = placeProps(this.level);
      this.props = new PropMeshes(props);
      this.scene.add(this.props.root);
      this.props.update(props, 0);
    }
  }

  /** A fresh skater at the spawn, on the session's stats, cheats and special sequences. */
  private newSkater(): Skater {
    const sk = new Skater(this.level);
    sk.stats = this.statSheet.stats;
    sk.cheats = this.settings.cheats;
    sk.specials = this.settings.specials;
    this.mountRide(sk, this.settings.ride);
    return sk;
  }

  /** On `ride` (#174), with it as the pack's item, so stepping off and back on keeps it. */
  private mountRide(sk: Skater, ride: RideId): void {
    sk.ride = ride;
    sk.pack.item = ride;
  }

  /** The lobby's ride pick (#174): saved as the default ride, and the skater waiting behind the lobby gets on it. */
  pickRide(ride: RideId): void {
    if (ride === this.settings.ride) return;
    this.applySettings({ ...this.settings, ride });
  }

  /**
   * A goal done for the first time this session pays its rung's stat points
   * (#66); returns what it paid. With a cheat on at any point since the run or
   * challenge started (#119) it doesn't count: null.
   */
  private goalDone(goalId: string): number | null {
    if (this.cheated) return null;
    const paid = this.board.complete(goalId);
    if (paid) this.statSheet.award(paid);
    return paid ?? 0;
  }

  /** "Tier 2 unlocked" (#86), added to the step's goal callout, once a goal opens a hub. */
  private announceUnlocks(): void {
    const tier = this.board.takeUnlocked();
    if (tier === undefined) return;
    const hub = this.board.openHub(tier);
    if (hub) this.guide = new HubGuide(hub);
    const text = `Tier ${tier} unlocked! Head to its hub`;
    const prev = this.message?.kind === "goal" ? `${this.message.text}  ·  ` : "";
    this.message = { id: ++this.messageId, kind: "goal", text: prev + text };
  }

  /** Starts demo mode: the scripted lines, one after another, on their maps. */
  startDemo(): void {
    this.running = true;
    this.menu.pause.hide();
    this.endRun();
    this.endChallenge();
    this.playDemoLine(0);
  }

  /** Skips to the next demo line (a tap on a phone). */
  nextDemoLine(): void {
    if (this.session.kind === "demo") this.playDemoLine(this.session.index + 1);
  }

  /** Back to skating: a fresh skater at the map's spawn. */
  stopDemo(): void {
    if (this.session.kind !== "demo") return;
    this.toFreeSkate();
    this.skater = this.newSkater();
    this.message = null;
    this.snapCamera();
    this.swallowOllie = true;
    this.exposeDev();
    // Demo maps are never mirrored: back to the flipped one if Mirror mode is on.
    if (this.mapMirrored !== this.settings.cheats.mirror) void this.loadMap(this.map.id);
  }

  /**
   * Starts a 2-minute run on this map (THPS career style): a fresh skater at
   * the spawn, the clock at 2:00 and the map's goal list to chase.
   */
  startRun(): void {
    if (!this.map) return; // nothing to skate yet
    this.leaveDemo();
    this.running = true;
    this.menu.pause.hide();
    this.skater = this.newSkater();
    this.endRun();
    this.endChallenge();
    this.startSession(new RunSession(this.host, this.map.goals, placeCollectables(this.map.goals, this.level)));
    this.refreshGoals();
    this.message = { id: ++this.messageId, kind: "goal", text: "2:00 · Go!" };
    this.pending = null;
    this.acc = 0;
    this.last = performance.now();
    this.swallowOllie = true;
    this.snapCamera();
    this.exposeDev();
  }

  /** Back to free skate, right where you are. */
  endRun(): void {
    if (this.session.kind !== "run") return;
    this.clearPickups();
    this.toFreeSkate();
    this.refreshGoals();
  }

  /** Back to free skate from whatever was on. */
  private toFreeSkate(): void {
    this.session = this.free;
  }

  /** Ends the demo, if it's on, without touching the skater (a map load or a run takes over). */
  private leaveDemo(): void {
    if (this.session.kind === "demo") this.toFreeSkate();
  }

  /** Puts a run or challenge on, with its pickups out in the park. */
  private startSession(session: RunSession | ChallengeSession): void {
    this.session = session;
    this.cheated = anyCheat(this.settings.cheats);
    const pickups = session.run.pickups;
    if (!pickups.length) return;
    this.pickups = new PickupMeshes(pickups);
    this.pickups.update(0, () => true);
    this.scene.add(this.pickups.root);
  }

  /** The challenge on, or null. */
  private get challenge(): ChallengeSession | null {
    return this.session.kind === "challenge" ? this.session : null;
  }

  /** The run or challenge on, or null. */
  private get goalSession(): GoalSession | null {
    return this.session.kind === "run" || this.session.kind === "challenge" ? this.session : null;
  }

  /** What the run and challenge sessions ask of the game. */
  private host: SessionHost = {
    play: (cue) => this.sfx.play(cue),
    say: (kind, text) => (this.message = { id: ++this.messageId, kind, text }),
    clearMessage: () => (this.message = null),
    goalDone: (id) => this.goalDone(id),
    refreshGoals: () => this.refreshGoals(),
  };

  private clearPickups(): void {
    if (!this.pickups) return;
    this.scene.remove(this.pickups.root);
    this.pickups.dispose();
    this.pickups = null;
  }

  /**
   * Sets up a challenge at its marker (THPS 4 style): the skater stands in
   * the marker facing what it's about, and the start card waits for X.
   */
  private startChallenge(spot: ChallengeSpot): void {
    this.endChallenge();
    this.skater.respawn(spot);
    // Every paintball target stands again (#137): only ones burst on its clock count.
    this.skater.pack.burst.clear();
    // ...and every graffiti tag is back (#151): only ones washed on its clock count.
    this.skater.pack.scrub.length = 0;
    this.skater.pack.cleaned.clear();
    this.chase.face(Math.sin(spot.yaw), Math.cos(spot.yaw));
    this.snapCamera();
    this.model.update(this.skater, 1, 0);
    this.blob.update(this.level, this.skater);
    this.vehicles?.update(this.level, this.skater.clock);
    this.road?.update(this.skater.clock, peopleRider(this.skater));
    this.startSession(new ChallengeSession(this.host, spot, this.map.goals, placeCollectables(this.map.goals, this.level)));
    this.message = null;
    this.pending = null;
    this.free.forget();
  }

  /** Opens a hub's list of its tier's challenges (#86): the sim holds still until one's picked or it's closed. */
  private openHub(hub: HubSpot): void {
    this.menu.hub.show(hub.tier, this.board.hubEntries(hub.tier));
    this.sfx.play("pick");
    this.pending = null;
  }

  /** Starts a challenge picked from a hub's list, at its own start spot. */
  pickHubChallenge(id: string): void {
    this.menu.hub.hide();
    const spot = this.board.spot(id);
    if (spot) this.startChallenge(spot);
  }

  /** Closes a hub's list without picking. */
  closeHub(): void {
    this.menu.hub.hide();
    this.swallowOllie = true;
    this.last = performance.now();
    this.acc = 0;
  }

  /** Moves the hub list's highlight (a hover). */
  setHubIndex(index: number): void {
    this.menu.hub.setIndex(index);
  }

  /** From a challenge's pass / fail card back to free skate at its tier's hub (#86). */
  backToHub(): void {
    const ch = this.challenge?.challenge;
    const hub = ch && this.board.hubFor(ch.spot.id);
    if (!ch || !hub) return;
    this.endChallenge();
    this.placeAtHub(hub);
  }

  /** Pause > Challenges' "Go to hub" (#87): free skate at an open tier's hub, facing its way. */
  goToHub(tier: Tier): void {
    const hub = this.board.openHub(tier);
    if (!hub || this.session.kind !== "free") return;
    this.placeAtHub(hub);
    this.swallowOllie = true;
  }

  /** Stands the skater in a hub, facing its way, the camera behind. */
  private placeAtHub(hub: HubSpot): void {
    this.skater.respawn(hub);
    this.chase.face(Math.sin(hub.yaw), Math.cos(hub.yaw));
    this.snapCamera();
    this.model.update(this.skater, 1, 0);
    this.blob.update(this.level, this.skater);
    this.vehicles?.update(this.level, this.skater.clock);
    this.road?.update(this.skater.clock, peopleRider(this.skater));
    this.last = performance.now();
    this.acc = 0;
  }

  /** Starts the challenge's clock (X on its start card). */
  beginChallenge(): void {
    const ch = this.challenge?.challenge;
    if (!ch || ch.phase !== "ready") return;
    ch.begin(this.skater.score);
    this.message = { id: ++this.messageId, kind: "goal", text: "Go!" };
    this.swallowOllie = true;
    this.pending = null;
    this.acc = 0;
    this.last = performance.now();
  }

  /** The same challenge again, from its marker. */
  retryChallenge(): void {
    if (this.challenge) this.startChallenge(this.challenge.challenge.spot);
  }

  /** Back to free skate, right where you are. */
  endChallenge(): void {
    if (!this.challenge) return;
    this.toFreeSkate();
    this.clearPickups();
    this.swallowOllie = true;
    this.refreshGoals();
  }

  /** Dev-only handle for poking at the sim from the console or a Playwright script. */
  private exposeDev(): void {
    if (import.meta.env.DEV) Object.assign(window, { __kickflip: { skater: this.skater, level: this.level, game: this } });
  }

  private playDemoLine(index: number): void {
    const i = index % DEMO_LINES.length;
    const line = DEMO_LINES[i]!;
    if (line.mapId !== this.map?.id || this.mapMirrored || this.loading) {
      // The line starts once its map is in (unless something else loads first), never mirrored.
      this.leaveDemo();
      void this.loadMap(line.mapId, false).then((ok) => {
        if (ok) this.playDemoLine(i);
      });
      return;
    }
    this.skater = demoSkater(this.level, line);
    this.session = new DemoSession(i, line);
    this.message = null;
    this.pending = null;
    this.acc = 0;
    this.model.update(this.skater, 1, 0);
    const f = this.skater.forward;
    this.chase.face(f.x, f.z);
    this.snapCamera();
    this.exposeDev();
  }

  /** The hill's pick card arrows (#203): the ride one along, while picking at the top. */
  hillPick(dir: -1 | 1): void {
    if (this.running && this.session.kind !== "demo" && !this.menu.pause.open && hillStepRide(this.skater, dir)) this.sfx.play("menu");
  }

  /** A tap on the pick card's ride (#203): drops in, like ✕. */
  hillDropIn(): void {
    if (this.running && this.session.kind !== "demo" && !this.menu.pause.open) hillDropIn(this.skater);
  }

  /** Starts Bomb the Hill's tutorial (#201) on the next run down the hill: call it after loading the hill. */
  startHillTutorial(): void {
    this.hill.startTutorial(this.level, randomSeed, this.skater.clock);
  }

  /** Skips the rest of the hill's tutorial (#201): it isn't offered by itself again. */
  skipHillTutorial(): void {
    this.hill.skipTutorial(this.level);
  }

  /** Opens the pause menu (from Start, or the tab losing focus). */
  openPause(): void {
    if (!this.running || this.menu.pause.open) return;
    this.menu.pause.show(this.pauseMode());
    this.pending = null;
  }

  private pauseMode(): PauseMode {
    return this.session.kind === "demo" ? "free" : this.session.kind;
  }

  runPauseAction(action: PauseAction): void {
    this.runIntents(this.menu.act(action, this.menuContext()));
  }

  /** What the menus need to know this frame. */
  private menuContext(): MenuContext {
    const ch = this.challenge?.challenge;
    const run = this.session.kind === "run" ? this.session.run : null;
    return {
      live: this.running && !this.loading,
      inRun: run !== null,
      results: run?.phase === "done",
      card: ch && !ch.live ? (ch.phase === "ready" ? "ready" : "over") : null,
      hubs: this.board.hasHubs,
      openHubs: this.board.openTiers(),
      demo: this.session.kind === "demo",
      mapId: this.map?.id ?? "",
      skaterId: this.skaterId,
      settings: this.settings,
      rebinding: this.settingsHud.rebinding,
      special: this.settingsHud.special ?? null,
      padPress: this.input.padPress,
      keyPress: this.input.keyPress,
    };
  }

  /** Plays a menu frame's sounds, then does what it asks. */
  private runMenu(step: MenuStep): void {
    for (const cue of step.cues) this.sfx.play(cue);
    this.runIntents(step.intents);
  }

  private runIntents(intents: readonly MenuIntent[]): void {
    for (const i of intents) {
      switch (i.type) {
        case "help":
          this.callbacks.onToggleHelp();
          break;
        case "settings":
          this.applySettings(i.settings);
          break;
        case "rebind":
          this.setRebinding(i.action);
          break;
        case "special":
          this.settingsHud = { ...this.settingsHud, special: i.edit };
          break;
        case "stat":
          if (!(i.up ? this.statSheet.raise(i.id) : this.statSheet.lower(i.id))) this.sfx.play("fail");
          break;
        case "startRun":
          this.startRun();
          break;
        case "endRun":
          this.endRun();
          break;
        case "beginChallenge":
          this.beginChallenge();
          break;
        case "retryChallenge":
          this.retryChallenge();
          break;
        case "endChallenge":
          this.endChallenge();
          break;
        case "pickChallenge":
          this.pickHubChallenge(i.id);
          break;
        case "closeHub":
          this.closeHub();
          break;
        case "toHub":
          this.backToHub();
          break;
        case "goHub":
          this.goToHub(i.tier);
          break;
        case "startDemo":
          this.startDemo();
          break;
        case "stopDemo":
          this.stopDemo();
          break;
        case "skater":
          this.selectSkater(i.id);
          break;
        case "map":
          this.leaveDemo();
          void this.loadMap(i.id);
          break;
        case "lobby":
          this.toLobby();
          break;
        case "reset":
          if (this.session.kind === "demo") this.stopDemo();
          this.skater.respawn();
          this.snapCamera();
          break;
        case "swallowOllie":
          this.swallowOllie = true;
          break;
        case "close":
          this.menu.pause.hide();
          this.swallowOllie = true;
          this.last = performance.now();
          this.acc = 0;
          break;
      }
    }
  }

  /** Mouse hover in the menu moves the highlight too. */
  setPauseIndex(index: number): void {
    this.menu.pause.index = index;
  }

  private onHidden = () => {
    if (document.visibilityState === "hidden") this.openPause();
  };
  private onBlur = () => this.openPause();
  /** Dev only: ` cycles the art style, for quick A/B looks (#75). */
  private onDevKey = (e: KeyboardEvent) => {
    if (e.code === "Backquote" && !e.repeat) this.applySettings(stepArtStyle(this.settings, 1));
  };
  private unlockAudio = () => this.sfx.unlock();

  /**
   * A pick from the HUD's quick-settings icons (#117): applied once (a
   * repeat pick of what's set does nothing), without pausing, and a new art
   * style gets the same "Applying…" wait as from Settings.
   */
  pickQuick<K extends QuickSetting>(key: K, value: Settings[K]): void {
    const next = pickQuick(this.settings, key, value);
    if (next) this.applySettings(next);
  }

  /** Keeps the pause menu's control rows in step with the device (a pad plugged in, touch forced on); a new object only on a change. */
  private updateControlRows(): void {
    const next = controlRows(this.settings.touch.mode, this.input.padName !== null);
    const cur = this.menu.pause.controls;
    if (next.touch !== cur.touch || next.buttons !== cur.buttons) this.menu.pause.controls = next;
  }

  /**
   * Uses new settings (bindings, volume; the camera reads them each frame)
   * and saves them. A new art style or quality rebuilds the park, the rider
   * or the post chain, which takes a moment, so that waits a couple of frames
   * (`LOOK_DELAY`) for "Applying…" to show, unless `now`.
   */
  private applySettings(s: Settings, now = false): void {
    // A new default ride: the skater waiting behind the lobby gets on it; while skating it's for the next fresh skater.
    if (s.ride !== this.settings.ride && this.skater && !this.running) this.mountRide(this.skater, s.ride);
    const lookChanged = s.artStyle !== this.settings.artStyle || s.quality !== this.settings.quality;
    this.settings = s;
    this.input.padMap = s.pad;
    this.input.keyMap = s.keys;
    this.sfx.volume = s.volume / VOLUME_MAX;
    if (now) this.applyLook();
    else if (lookChanged) this.lookDue = LOOK_DELAY;
    this.bailFx.style = s.bailStyle;
    this.trickFx.sparks = s.grindSparks;
    this.trickFx.landing = s.landingFx;
    this.trickFx.trail = s.airTrail;
    this.trickFx.finisher = s.finisher;
    // Demo skaters ride without cheats; turning one on taints the run or challenge on.
    if (this.skater && this.session.kind !== "demo") this.skater.cheats = s.cheats;
    if (this.skater && this.session.kind !== "demo") this.skater.specials = s.specials;
    if (anyCheat(s.cheats)) this.cheated = true;
    // Mirror mode flipped: the map loads again, flipped or not (like picking a new map).
    const mapId = this.loading?.id ?? this.map?.id;
    const mirrored = this.loading ? this.loadingMirrored : this.mapMirrored;
    if (mapId && this.session.kind !== "demo" && s.cheats.mirror !== mirrored) void this.loadMap(mapId, s.cheats.mirror);
    this.settingsHud = { ...this.settingsHud, settings: s };
    saveSettings(storage(), s);
  }

  /** Builds the settings' art style and quality (a no-op if they're what's on screen). */
  private applyLook(): void {
    this.lookDue = 0;
    this.useStyle(this.settings.artStyle);
    const quality = effectiveQuality(this.settings.quality, touchOnly());
    this.stage.setQuality(quality);
    // Low quality trims the fireworks bail (#199).
    this.bailFx.lite = quality === "low";
  }

  private setRebinding(a: Action | null): void {
    this.settingsHud = { ...this.settingsHud, rebinding: a };
  }

  dispose(): void {
    window.removeEventListener("blur", this.onBlur);
    document.removeEventListener("visibilitychange", this.onHidden);
    window.removeEventListener("pointerdown", this.unlockAudio, true);
    window.removeEventListener("keydown", this.unlockAudio, true);
    window.removeEventListener("keydown", this.onDevKey);
    this.sfx.dispose();
    this.bailFx.dispose();
    this.trickFx.dispose();
    this.paintFx.dispose();
    this.washFx.dispose();
    this.crosshair.dispose();
    this.targets?.dispose();
    this.graffiti?.dispose();
    this.props?.dispose();
    cancelAnimationFrame(this.raf);
    this.resizeObserver.disconnect();
    this.input.dispose();
    this.stage.dispose();
  }

  private resize(): void {
    const w = this.canvas.clientWidth || 1;
    const h = this.canvas.clientHeight || 1;
    this.stage.resize(w, h);
    this.chase.camera.aspect = w / h;
    this.chase.camera.updateProjectionMatrix();
    // On a phone the hill's camera sits further back, for more road over the touch keys (#198).
    this.chase.roomy = phoneScreen(touchOnly(), h);
    // Held upright (#213), the hill's camera frames the road for a tall screen.
    this.chase.tall = h > w;
  }

  private frame(dt: number): void {
    let input = this.input.poll();
    if (input.pressed.help) this.callbacks.onToggleHelp();

    // A new art style or quality, once "Applying…" has had a frame to paint.
    if (this.lookDue > 0 && --this.lookDue === 0) this.applyLook();

    // Nothing to show until the first map is in.
    if (!this.map) return;

    // Paused, loading, not started, or a run's results or a challenge's card
    // up: keep drawing, run the menu or the card, move nothing.
    if (!this.running || this.menu.pause.open || this.loading || this.session.holding || this.menu.hub.open) {
      this.sfx.setLoops(loopLevels(null));
      this.updateControlRows();
      this.runMenu(this.menu.frame(input, this.menuContext()));
      // Behind the lobby the rider stands still but keeps posing, so a ride picked there (#174) shows on the turntable.
      if (!this.running) this.model.update(this.skater, dt, 0);
      this.spinTurntable(dt);
      this.updateMarkers(dt);
      this.stage.render(this.chase.camera);
      this.emitHud(input);
      return;
    }
    if (input.pressed.pause && !startDropsIn(this.skater.hill, input)) {
      this.openPause();
      this.emitHud(input);
      return;
    }
    // A play button stops the demo; △ in a marker starts its challenge, in a hub opens its list (the press doesn't reach the sim).
    const press = this.session.onPress(input);
    if (press?.type === "stopDemo") this.stopDemo();
    else if (press?.type === "challenge" || press?.type === "hub") {
      if (press.type === "hub") this.openHub(press.hub);
      else this.startChallenge(press.spot);
      this.stage.render(this.chase.camera);
      this.emitHud(input);
      return;
    }
    if (this.swallowOllie) {
      if (input.held.ollie || input.released.ollie) input = withoutOllie(input);
      else this.swallowOllie = false;
    }

    // Press/release edges must survive frames where no physics step runs
    // (high refresh displays), so merge them until a step consumes them.
    this.pending = this.pending ? mergeEdges(this.pending, input) : input;
    const heldOnly = stripEdges(input);

    const events: SkaterEvent[] = [];
    // Slow-mo (#119) slows everything that moves with the game; the camera keeps real time.
    const gdt = this.session.kind === "demo" ? dt : dt * gameSpeed(this.settings.cheats);
    this.acc += gdt;
    let steps = 0;
    let lean = input.moveX;
    const picking = this.skater.hill?.picking ?? false;
    // On a hill (#193) the rider leans with the carve, which eases in.
    const carving = () => (this.skater.hill && this.skater.mode === "ground" ? this.skater.hill.carve : null);
    while (this.acc >= STEP && steps < MAX_STEPS) {
      const scripted = this.session.kind === "demo" ? this.session.next(this.skater) : null;
      if (scripted) lean = scripted.moveX;
      const stepEvents = this.skater.step(scripted ?? this.pending ?? heldOnly, STEP);
      events.push(...stepEvents);
      this.stepSound(stepEvents);
      this.session.touch(this.skater);
      this.pending = null;
      this.acc -= STEP;
      steps++;
    }
    if (steps === MAX_STEPS) this.acc = 0;
    // Dropped in on the hill with ✕ (#207): that press isn't an ollie too when it's let go.
    if (picking && !this.skater.hill?.picking) this.swallowOllie = true;
    for (const e of events) this.handleEvent(e);
    this.updateHint(steps * STEP);
    this.session.step(steps * STEP, this.skater, events);
    this.announceUnlocks();
    if (this.guide && this.freeSkating() && !this.guide.step(steps * STEP, this.skater.x, this.skater.z)) this.guide = null;
    if (steps > 0 && this.session.kind !== "demo" && this.hill.stepTutorial(this.skater, this.level, steps * STEP * 1000)) this.sfx.play("menu");
    if (this.session.kind !== "free") this.free.forget();
    if (this.session.kind === "demo" && this.session.done) this.nextDemoLine();

    this.menu.simRan();
    this.spinTurntable(0);
    this.sfx.setLoops(loopLevels(soundSnap(this.skater)));
    const carve = carving();
    this.model.update(this.skater, gdt, carve === null ? lean : carve * CARVE_LEAN);
    this.shakeRider();
    const bailCue = this.bailFx.update(this.skater, this.model, findSkater(this.skaterId).look, gdt, this.floorAt);
    if (bailCue) this.sfx.play(bailCue);
    this.finish.step(gdt);
    const finisher = this.trickFx.update(this.skater, this.fxCues, gdt * this.finish.fxScale, this.floorAt, events);
    if (finisher === "cheer") this.sfx.play("cheer");
    else if (finisher === "slowmo") this.finish.start();
    this.fxCues.length = 0;
    this.paintFx.update(this.skater.pack.balls, events);
    this.washFx.update(this.skater.pack.jet, gdt);
    this.blob.update(this.level, this.skater);
    this.vehicles?.update(this.level, this.skater.clock);
    this.road?.update(this.skater.clock, peopleRider(this.skater));
    this.targets?.update(this.skater.pack.burst, gdt);
    this.graffiti?.update(this.skater.pack, events, gdt);
    this.props?.update(this.skater.pack.props, gdt);
    const goal = this.goalSession;
    if (this.pickups && goal) this.pickups.update(gdt, (c) => goal.run.shown(c));
    this.updateMarkers(gdt);
    this.updateCamera(dt);
    this.crosshair.update(this.skater, this.chase.camera);
    this.stage.render(this.chase.camera);
    this.emitHud(input);
  }

  private handleEvent(e: SkaterEvent): void {
    if (e.type === "landed") {
      // THPS calls out how you came down: "Sloppy!" / "Perfect!" ahead of the combo.
      const call = e.landing === "sloppy" ? "Sloppy!  " : e.landing === "perfect" ? "Perfect!  " : "";
      // On the hill (#205) it's what the combo added to the run's trick score, nothing near the top or rolling slow.
      const pts = e.hill === undefined ? `+${e.points.toLocaleString()}` : e.hill > 0 ? `+${e.hill.toLocaleString()} trick pts` : "no points: get rolling!";
      this.message = { id: ++this.messageId, kind: "landed", text: `${call}${e.combo}  ${pts}` };
    } else if (e.type === "gap") {
      this.message = { id: ++this.messageId, kind: "gap", text: `${e.name}  +${e.points.toLocaleString()}` };
      if (this.session.kind !== "demo") {
        const found = this.gapsFound.get(this.map.id) ?? new Set<string>();
        this.gapsFound.set(this.map.id, found.add(e.name));
        this.refreshGaps();
      }
    } else if (e.type === "target") {
      this.message = { id: ++this.messageId, kind: "gap", text: e.burst === e.of ? `All ${e.of} targets!` : `Target!  ${e.burst} / ${e.of}` };
    } else if (e.type === "clean") {
      this.message = { id: ++this.messageId, kind: "gap", text: e.cleaned === e.of ? `All ${e.of} tags washed off!` : `Squeaky clean!  ${e.cleaned} / ${e.of}` };
    } else if (e.type === "hill") {
      // A run down the hill (#193) ended: the HUD's card says how far, and the bests are kept.
      this.hill.ended(this.map.id, e);
    } else if (e.type === "bail") {
      // On a hill the run's end card says what happened instead.
      if (!this.skater.hill) this.message = { id: ++this.messageId, kind: "bail", text: e.wipeout ? "WIPEOUT!" : "BAIL!" };
    } else if (e.type === "hillClose") {
      // Whipped past someone on a crosswalk (#196): they shout, and it counts.
      this.message = { id: ++this.messageId, kind: "gap", text: `HEY!  Close call +${e.bonus} m` };
    } else if (e.type === "hillShave") {
      // Whipped past a side street's car (#197): it counts too.
      this.message = { id: ++this.messageId, kind: "gap", text: `CLOSE SHAVE!  +${e.bonus} m` };
    } else if (e.type === "hillShift") {
      // The hill's road moved back up under the rider (#195): the camera moves with it.
      this.chase.shift(e.dz, e.dy);
    } else if (e.type === "respawn") {
      this.snapCamera();
    }
  }

  /**
   * Behind the start screen and Pause > Skater the rider turns slowly on the
   * spot so you can see the whole outfit; anywhere else they face their line.
   */
  private spinTurntable(dt: number): void {
    const showing = !this.running || (this.menu.pause.open && this.menu.pause.screen === "skater");
    this.turntable = showing && dt > 0 ? (this.turntable + dt * 0.9) % (Math.PI * 2) : 0;
    this.model.root.rotation.y = this.turntable;
  }

  /** Shows the markers in free skate only, checked when done, the one the skater's in pulsing. */
  private updateMarkers(dt: number): void {
    if (!this.markers) return;
    this.markers.root.visible = this.freeSkating() && !this.level.hill;
    const near = this.free.nearHub?.id ?? this.nearSpot?.id ?? null;
    if (this.markers.root.visible) this.markers.update(dt, this.board.markerDone, near, this.board.locked);
  }

  /** Free skate proper: no run, challenge or demo, so the markers are out. */
  private freeSkating(): boolean {
    return this.session.kind === "free";
  }

  /** The marker the skater's in, if one could start now (kept until the next sim step outside free skate). */
  private get nearSpot(): ChallengeSpot | null {
    return this.free.near;
  }

  /** One sim step's sound effects. */
  private stepSound(events: readonly SkaterEvent[]): void {
    if (this.cueSkater !== this.skater) {
      this.cues.reset();
      this.cueSkater = this.skater;
    }
    // On a phone the landings, grinds and bails buzz too (#67), but not in the demo.
    const buzz = this.input.source === "touch" && this.session.kind !== "demo";
    for (const cue of this.cues.update(soundSnap(this.skater), events)) {
      this.sfx.play(cue, this.skater.combo.multiplier);
      this.fxCues.push(cue);
      const pattern = buzz ? HAPTICS[cue] : undefined;
      if (pattern !== undefined) vibrate(pattern);
    }
  }

  /**
   * Rebuilds the goal list for the HUD (call it when a goal is done, so the
   * HUD diff sees a new array): the run's, or outside one the map's, checked
   * off if done this session in a run or a challenge.
   */
  private refreshGoals(): void {
    const goals = this.session.goals();
    if (goals) {
      this.goalList = goals;
      return;
    }
    // Free skate: the map's challenges as a ladder (#66), by tier, with locks and what each pays.
    this.goalList = this.board.goals();
  }

  /** Rebuilds the Gap list (a new array only when something changed, for the HUD diff). */
  private refreshGaps(): void {
    const found = this.gapsFound.get(this.map.id);
    this.gapList = (this.level.gaps ?? []).map((g) => ({ name: g.name, points: g.points, found: found?.has(g.name) ?? false }));
  }

  private snapCamera(): void {
    this.finish.reset();
    this.viewFov = chaseFov(this.chase.camera.aspect);
    this.chase.setFov(this.viewFov);
    this.chase.snap(this.skater, this.level, this.map.look.roof);
    this.stage.setFocus(0, 0);
  }

  private updateCamera(dt: number): void {
    const s = this.skater;
    this.chase.update(s, dt, CAMERA_RIGS[this.settings.camera], this.level, this.map.look.roof);
    // Upright the view opens up so you see as much across (#235), easing in after a rotate.
    this.viewFov += (chaseFov(this.chase.camera.aspect) - this.viewFov) * (1 - Math.exp(-FOV_EASE * dt));
    // On a hill (#194) the view widens with speed.
    this.chase.setFov(this.finish.fov(this.viewFov + (this.level.hill ? hillView(s.horizontalSpeed).fov : 0)));
    // The finisher's depth of field (#179): sharp on the rider's chest, the park behind softening as it punches in.
    const cam = this.chase.camera.position;
    this.stage.setFocus(Math.hypot(s.x - cam.x, s.y + 0.9 - cam.y, s.z - cam.z), this.finish.focus);
    // Speed wobbles (#193) shake the camera, harder the closer they are to throwing you.
    const wobble = s.hill?.wobble ?? 0;
    if (wobble > 0) {
      const k = WOBBLE_CAM * wobble * wobble;
      const cam = this.chase.camera;
      cam.position.x += Math.sin(s.clock * 41) * k;
      cam.position.y += Math.sin(s.clock * 57 + 1) * k * 0.6;
    }
    // Keep the shadow map centered on the skater (down the hill with them on one, #193).
    this.stage.follow(s.x, s.z, -hillDrop(this.level, s.z));
  }

  /**
   * Speed wobbles (#193): the rider and board shake side to side, faster and
   * wider as they build. Render only, off the sim's clock.
   */
  private shakeRider(): void {
    const sk = this.skater;
    const w = sk.hill?.wobble ?? 0;
    if (w <= 0 || sk.mode !== "ground") return;
    // Rocked about the way the board points (the turntable's turn is 0 while skating).
    const f = sk.forward;
    this.shakeAxis.set(f.x, 0, f.z);
    this.model.root.quaternion.setFromAxisAngle(this.shakeAxis, Math.sin(sk.clock * (18 + 14 * w)) * WOBBLE_TILT * w);
  }

  /** The guide arrow (#87), in free skate only, relative to where the camera looks. */
  private guideHud(): HubGuideHud | null {
    if (!this.guide || !this.freeSkating()) return null;
    const d = this.chase.camera.getWorldDirection(this.camDir);
    const len = Math.hypot(d.x, d.z) || 1;
    return this.guide.hud(this.skater, { x: d.x / len, z: d.z / len });
  }

  private runOutHud(): number | null {
    const left = runOutLeft(this.skater);
    return left === null ? null : Math.ceil(left * 50) / 50;
  }

  /**
   * Getting on the bike for the first time shows how its flips go (#150),
   * once ever: the hint is marked seen in settings as it shows. Not for the
   * demo's rider.
   */
  private updateHint(dt: number): void {
    const riding = this.skater.foot ? null : this.skater.ride;
    const due = this.session.kind === "demo" ? null : dueHint(this.rideWas, riding, this.settings.hintsSeen);
    this.rideWas = riding;
    if (due) {
      this.hint = { id: due, left: HINT_TIME };
      this.applySettings({ ...this.settings, hintsSeen: [...this.settings.hintsSeen, due] });
    } else if (this.hint && (this.hint.left -= dt) <= 0) this.hint = null;
  }

  /** An action's button on the input you're using. */
  private buttonLabel(source: InputFrame["source"], a: keyof typeof TOUCH_LABELS): string {
    return source === "gamepad" ? padLabel(this.input.padMap[a]) : source === "touch" ? TOUCH_LABELS[a] : keyLabel(this.input.keyMap[a][0] ?? "");
  }

  /** The pack strip's line ("L2 + R2 to ride · L1 / R1 switch") in whatever the player is using, through their bindings (#136, #145). */
  private packKeys(source: InputFrame["source"], item: ItemId): string {
    return packKeysText(item, (a) => this.buttonLabel(source, a));
  }

  private emitHud(input: InputFrame): void {
    const s = this.skater;
    const next: HudState = {
      score: s.score,
      bestCombo: s.bestCombo,
      bestComboText: s.combos.best?.text ?? "",
      recentCombos: s.combos.recent,
      special: Math.round(s.special.value * 100) / 100,
      specialFull: s.special.special,
      comboText: s.combo.describe(),
      comboBase: Math.round(s.combo.base),
      comboMult: s.combo.multiplier,
      balance: s.grind || s.lip || s.skitch ? Math.round((s.grind ?? s.lip ?? s.skitch)!.balance * 50) / 50 : null,
      manualBalance: s.manual ? Math.round(s.manual.balance * 50) / 50 : null,
      runOut: this.runOutHud(),
      packItem: heldItem(s),
      packKeys: heldItem(s) ? this.packKeys(input.source, heldItem(s)!) : null,
      ride: s.ride,
      rideHint: this.hint ? hintText(this.hint.id, input.source, this.buttonLabel(input.source, "flip")) : null,
      message: this.message,
      padName: this.input.padName,
      padSeen: this.input.padSeen,
      padBlocked: this.input.padBlocked,
      stance: stanceReadout(s),
      ...touchHints(s),
      source: input.source,
      speed: Math.round(s.horizontalSpeed * 3.6),
      paused: this.menu.pause.open,
      pauseIndex: this.menu.pause.index,
      pauseScreen: this.menu.pause.screen,
      mapId: this.map.id,
      loading: this.loading?.name ?? null,
      gaps: this.gapList,
      demo: this.session.kind === "demo" ? this.session.name : null,
      run: this.session.kind === "run" ? this.session.hud() : null,
      goals: this.goalList,
      challenge: this.challenge?.hud() ?? null,
      settings: this.settingsHud,
      stats: this.statSheet.hud(),
      skater: this.skaterId,
      hill: this.hill.hud(s, this.map.id),
      prompt: this.free.prompt(this.board.markerDone),
      hub: this.menu.hub.hud,
      hasHubs: this.board.hasHubs,
      guide: this.guideHud(),
      pauseHubs: this.menu.pause.hubs,
      pauseControls: this.menu.pause.controls,
      applying: this.lookDue > 0,
    };
    const prev = this.hud;
    if (
      prev &&
      (Object.keys(next) as (keyof HudState)[]).every((k) => prev[k] === next[k])
    ) {
      return;
    }
    this.hud = next;
    this.callbacks.onHud(next);
  }
}

/** The on-screen keys' labels for the backpack's buttons (`touch.ts`). */
const TOUCH_LABELS = { spinL: "L", spinR: "R", flip: "Flip", nollie: "Nollie", revert: "Revert" } as const;

function withoutOllie(f: InputFrame): InputFrame {
  return {
    ...f,
    held: { ...f.held, ollie: false },
    pressed: { ...f.pressed, ollie: false },
    released: { ...f.released, ollie: false },
  };
}

function stripEdges(f: InputFrame): InputFrame {
  const none = Object.fromEntries(BUTTONS.map((k) => [k, false])) as InputFrame["pressed"];
  return { ...f, pressed: none, released: { ...none } };
}

function mergeEdges(a: InputFrame, b: InputFrame): InputFrame {
  const pressed = { ...b.pressed };
  const released = { ...b.released };
  for (const k of BUTTONS) {
    pressed[k] ||= a.pressed[k];
    released[k] ||= a.released[k];
  }
  return { ...b, pressed, released };
}

/** A short buzz where the browser has one (Android; not iOS Safari). */
function vibrate(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Some browsers refuse without a recent tap; the buzz is only a nicety.
  }
}

/**
 * Settings > Controls on this device (#126): the touch rows where there's a
 * touchscreen (or the touch controls are forced On, so they can be turned
 * back off), the bindings where there's a keyboard or a pad.
 */
function controlRows(touchMode: string, pad: boolean): ControlRows {
  const only = touchOnly();
  const screen = only || (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0);
  return { touch: screen || touchMode === "on", buttons: !only || pad };
}

